import { useCallback, useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';

import { useQuery } from '#hooks/useQuery';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { deducirNomina, parseConfig, parsePlanes } from './nomina';
import type {
  ConfigNomina,
  IngresoHistorial,
  NominaDeducida,
  PlanesPagas,
  PlanPaga,
} from './nomina';

/** Pref sincronizada `nomina`: la configuración de la nómina. */
export function useConfigNomina() {
  const [raw, setRaw] = useSyncedPref('nomina');
  const guardada = useMemo(() => parseConfig(raw), [raw]);
  // Confirmada desde el historial: se pone al día sola con lo que se cobra.
  const deducida = useNominaDeducida(guardada?.auto === true);
  const config = useMemo(
    () =>
      guardada?.auto && deducida
        ? {
            ...guardada,
            tipo: deducida.tipo,
            neta: deducida.neta,
            dia: deducida.dia,
            pagas: deducida.pagas,
          }
        : guardada,
    [guardada, deducida],
  );
  const guardar = useCallback(
    (nueva: ConfigNomina) => setRaw(JSON.stringify(nueva)),
    [setRaw],
  );
  // Quien paga la nómina (solo si se dedujo): así lo que llega se mira de él.
  const pagador = guardada?.auto ? (deducida?.pagador ?? null) : null;
  return { config, guardar, pagador };
}

/** Pref sincronizada `pagas-plan`: plan y estado de cada paga de cada año. */
export function usePlanesPagas() {
  const [raw, setRaw] = useSyncedPref('pagas-plan');
  const planes = useMemo(() => parsePlanes(raw), [raw]);
  const guardarPlan = useCallback(
    (clave: string, plan: PlanPaga) => {
      // Se leen los planes del momento: así dos guardados seguidos no se pisan.
      const actuales: PlanesPagas = parsePlanes(raw);
      setRaw(JSON.stringify({ ...actuales, [clave]: plan }));
    },
    [raw, setRaw],
  );
  return { planes, guardarPlan };
}

/**
 * Ingresos de los últimos 12 meses cerrados (todo lo que entra en cuentas
 * del presupuesto que no sea un traspaso entre cuentas propias). Con ellos
 * Rumbo deduce la nómina: quién paga, cuánto, qué día y en qué meses dobla.
 */
export function useNominaDeducida(activo: boolean): NominaDeducida | null {
  const mesActual = monthUtils.currentMonth();
  const desde = monthUtils.subMonths(mesActual, 12);
  const { data } = useQuery<IngresoHistorial>(
    () =>
      activo
        ? q('transactions')
            .filter({
              amount: { $gt: 0 },
              transfer_id: null,
              'account.offbudget': false,
              date: { $gte: `${desde}-01`, $lt: `${mesActual}-01` },
            })
            .select(['date', 'amount', 'payee'])
        : null,
    [activo, desde, mesActual],
  );
  return useMemo(() => (data ? deducirNomina(data) : null), [data]);
}

/**
 * Lo que entró en el mes (sin traspasos), para saber si llegó la paga. Con
 * `pagador`, solo lo suyo (otros ingresos no se toman por la paga).
 */
export function useIngresosDelMes(
  mes: string | null,
  pagador: string | null = null,
): number | null {
  const { data, isLoading } = useQuery<{ amount: number }>(
    () =>
      mes
        ? q('transactions')
            .filter({
              amount: { $gt: 0 },
              transfer_id: null,
              'account.offbudget': false,
              date: { $transform: '$month', $eq: mes },
              ...(pagador ? { payee: pagador } : {}),
            })
            .select(['amount'])
        : null,
    [mes, pagador],
  );
  return useMemo(() => {
    if (!mes || isLoading) {
      return null;
    }
    return (data ?? []).reduce((s, f) => s + f.amount, 0);
  }, [data, isLoading, mes]);
}
